"use client";

// Next.js imports
import dynamic from "next/dynamic";

import type { Dictionary } from "@/dictionaries";
import {
  ReadableCopy,
  ReadableValue,
  readableFallback,
} from "@/components/readable";

interface FeaturedSectionProps {
  theme?: string;
  data: any;
  locale?: string;
  skeleton?: boolean;
  priority?: boolean;
  dict?: Dictionary;
}

// Dynamic Component Imports — ssr: false prevents hydration mismatches from
// Stencil connectedCallback inserting s-cr comment nodes before React reconciles children.
const placeholder = readableFallback("min-h-[200px]");
const tallPlaceholder = readableFallback("min-h-[500px]");

const SectionPlaceholder = dynamic(
  () => import("@/templates/SectionPlaceholder"),
  { ssr: false, loading: placeholder },
);
const TextStack = dynamic(() => import("@/templates/TextStack"), {
  ssr: false,
  loading: placeholder,
});
const RiverItem = dynamic(() => import("@/templates/RiverItem"), {
  ssr: false,
  loading: tallPlaceholder,
});
const BodyCTA = dynamic(() => import("@/templates/BodyCTA"), {
  ssr: false,
  loading: placeholder,
});
const BodySubscribe = dynamic(() => import("@/templates/BodySubscribe"), {
  ssr: false,
  loading: placeholder,
});
const CardGrid = dynamic(() => import("@/templates/CardGrid"), {
  ssr: false,
  loading: tallPlaceholder,
});
const TextGrid = dynamic(() => import("@/templates/TextGrid"), {
  ssr: false,
  loading: tallPlaceholder,
});
const LogoBar = dynamic(() => import("@/templates/LogoBar"), {
  ssr: false,
  loading: placeholder,
});
const Quote = dynamic(() => import("@/templates/Quote"), {
  ssr: false,
  loading: placeholder,
});
const Agenda = dynamic(() => import("@/templates/Agenda"), {
  ssr: false,
  loading: tallPlaceholder,
});
const PhotoGallery = dynamic(() => import("@/templates/PhotoGallery"), {
  ssr: false,
  loading: tallPlaceholder,
});
const ComparisonTable = dynamic(() => import("@/templates/ComparisonTable"), {
  ssr: false,
  loading: tallPlaceholder,
});
const FAQ = dynamic(() => import("@/templates/FAQ"), {
  ssr: false,
  loading: tallPlaceholder,
});
const ContentSwitcher = dynamic(() => import("@/templates/ContentSwitcher"), {
  ssr: false,
  loading: tallPlaceholder,
});
const AnimationScroller = dynamic(
  () => import("@/templates/AnimationScroller"),
  {
    ssr: false,
    loading: readableFallback("min-h-[600px]"),
  },
);

export default function FeaturedSection(props: FeaturedSectionProps) {
  return (
    <ReadableCopy
      copy={<ReadableValue value={props.data} locale={props.locale ?? "en"} />}
    >
      <FeaturedSectionContent {...props} />
    </ReadableCopy>
  );
}

function FeaturedSectionContent({
  theme,
  data,
  locale = "en",
  skeleton,
  priority,
  dict,
}: FeaturedSectionProps) {
  if (data?._type === "textStack")
    return (
      <TextStack
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "riverItem")
    return (
      <RiverItem
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
        priority={priority}
      />
    );

  if (data?._type === "bodyCta")
    return (
      <BodyCTA
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "bodySubscribe")
    return (
      <BodySubscribe
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
        dict={dict}
      />
    );

  if (data?._type === "cardGrid")
    return (
      <CardGrid
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "textGrid")
    return (
      <TextGrid
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "logoBar")
    return (
      <LogoBar
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "quote")
    return (
      <Quote
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "imageScroller")
    return (
      <LogoBar
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
        hasSpace
      />
    );

  if (data?._type === "agenda")
    return (
      <Agenda
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
        dict={dict}
      />
    );

  if (data?._type === "mediaGallery")
    return (
      <PhotoGallery
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "comparisonTable")
    return (
      <ComparisonTable
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "faq")
    return (
      <FAQ
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "contentSwitcher")
    return (
      <ContentSwitcher
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  if (data?._type === "animationScroller")
    return (
      <AnimationScroller
        {...data}
        theme={data?.theme || theme}
        locale={locale}
        skeleton={skeleton}
      />
    );

  return (
    <SectionPlaceholder
      theme={theme}
      eyebrow={data?._type}
      title={data?.title}
      description={data?.description}
      skeleton={skeleton}
    />
  );
}
